<template>
  <div class="page">
    <header class="topbar">
      <div class="brand">
        <img class="logo" src="@/assets/logo.png" alt="logo"/>
        <div class="titles">
          <div class="name">SNIPER BOT</div>
          <div class="sub">Trader Fetch · Frontend Deploy · Start Bot</div>
        </div>
      </div>

      <button class="ghost" @click="hintOn = !hintOn">
        {{ hintOn ? "HINT:ON" : "HINT:OFF" }}
      </button>
    </header>

    <main class="scroll">
      <div class="content">
        <section v-if="hintOn" class="notice">
          <div class="notice-title">安全提示</div>
          <div class="notice-text">
            本页面不收集私钥。合约部署/提取/紧急卖出均由用户钱包在浏览器端签名完成；请确认网络、合约字节码与参数无误后再执行。
          </div>
        </section>

        <!-- 0) 参数 -->
        <section class="card">
          <div class="card-h">
            <div class="card-title">机器人参数</div>
            <div class="badge">
              {{
                isScanMode ? "mode=SCAN · amount · profitRate · payToken" : "targetToken · amount · profitRate · payToken"
              }}
            </div>
          </div>

          <div class="form">
            <!-- ✅ NEW: mode switch -->
            <label class="field">
              <span class="label">模式（mode）</span>

              <div class="mode-toggle">
                <button
                    class="chip"
                    :class="{ on: form.mode === 'TARGET' }"
                    @click.prevent="setMode('TARGET')"
                    type="button"
                >
                  指定目标
                </button>

                <button
                    class="chip"
                    :class="{ on: form.mode === 'SCAN' }"
                    @click.prevent="setMode('SCAN')"
                    type="button"
                >
                  全网监听
                </button>
              </div>

              <div class="helper mono" v-if="isScanMode">
                扫描模式下无需填写 targetToken；由后端机器人全网扫描并决定交易对象。
              </div>
            </label>

            <!-- ✅ TARGET 模式才显示 targetToken -->
            <label class="field" v-if="!isScanMode">
              <span class="label">目标 Token（targetToken）</span>
              <input v-model.trim="form.targetToken" class="input mono" placeholder="目标 Token 合约地址（0x...）"/>
            </label>

            <label class="field">
              <span class="label">支付币种（payToken）</span>
              <select v-model="form.payToken" class="input mono" @change="onLayoutMaybeChanged">
                <option value="USDT">USDT</option>
                <option value="BNB">BNB</option>
              </select>
            </label>

            <div class="row2">
              <label class="field">
                <span class="label">购买数量</span>
                <input v-model.number="form.amount" class="input mono" type="number" inputmode="decimal"
                       placeholder="100"/>
              </label>

              <label class="field">
                <span class="label">赢利率 (%)</span>
                <input v-model.number="form.profitRate" class="input mono" type="number" inputmode="decimal"
                       placeholder="1.5"/>
              </label>
            </div>

            <div class="helper mono">
              获取 trader 与启动机器人都需要 withdrawer（当前钱包地址）。
              <span v-if="!isScanMode">同时需要 targetToken。</span>
              <span v-else>扫描模式不需要 targetToken。</span>
            </div>
          </div>
        </section>

        <!-- 1) 钱包连接 + trader 获取 -->
        <section class="card">
          <div class="card-h">
            <div class="card-title">连接与权限地址</div>
            <div class="badge">withdrawer=user · trader=backend</div>
          </div>

          <div class="kv">
            <div class="k">withdrawer</div>
            <div class="v mono ell">{{ userWallet || "-" }}</div>

            <div class="k">network</div>
            <div class="v mono">{{ chainLabel }}</div>

            <div class="k">trader</div>
            <div class="v mono ell">{{ traderWallet || "-" }}</div>
          </div>

          <div class="ops-grid">
            <button class="btn" :disabled="connecting" @click="connectWallet">
              {{ userWallet ? "已连接" : (connecting ? "CONNECTING..." : "连接钱包") }}
            </button>

            <button class="btn" :disabled="!canFetchTrader || loadingTrader" @click="fetchTraderWallet">
              {{ loadingTrader ? "FETCHING..." : "获取 trader 地址" }}
            </button>

            <button class="btn" :disabled="!traderWallet" @click="copyText(traderWallet, '已复制 trader')">
              复制 trader
            </button>
          </div>

          <div class="helper mono">
            注意：获取 trader 需要已连接钱包。
            <span v-if="!isScanMode">并填写 targetToken。</span>
            <span v-else>扫描模式无需填写 targetToken。</span>
          </div>
        </section>

        <!-- 2) 部署合约 -->
        <section class="card">
          <div class="card-h">
            <div class="card-title">合约部署</div>
            <div class="badge">deploy(trader, withdrawer)</div>
          </div>

          <div class="kv">
            <div class="k">contract</div>
            <div class="v mono ell">{{ deployedContract || "-" }}</div>

            <div class="k">deployTx</div>
            <div class="v mono ell">{{ deployTxHash || "-" }}</div>

            <div class="k">deployStatus</div>
            <div class="v mono" :class="deployStatusClass">{{ deployStatus }}</div>
          </div>

          <div class="ops-grid">
            <button class="btn" :disabled="!canDeploy || deploying" @click="deployContract">
              {{ deploying ? "DEPLOYING..." : "部署合约" }}
            </button>

            <button class="btn" :disabled="!deployedContract" @click="copyText(deployedContract, '已复制合约地址')">
              复制合约地址
            </button>

            <button class="btn" :disabled="!deployedContract" @click="openExplorer(deployedContract)">
              浏览器查看
            </button>
          </div>

          <div class="helper mono">
            部署前必须：已连接钱包 + 已获取 trader 地址。部署成功后会显示合约地址。
          </div>
        </section>

        <!-- ✅ 合约资产（10 秒刷新） -->
        <section class="card">
          <div class="card-h">
            <div class="card-title">合约资产</div>
            <div class="badge mono">balances · 10s refresh</div>
          </div>

          <div class="kv">
            <div class="k">WBNB</div>
            <div class="v mono ell">{{ balances.wbnbText }}</div>

            <div class="k">USDT</div>
            <div class="v mono ell">{{ balances.usdtText }}</div>

            <div class="k">TargetToken</div>
            <div class="v mono ell">{{ balances.targetText }}</div>

            <div class="k">updated</div>
            <div class="v mono">{{ balances.updatedAt || "-" }}</div>
          </div>

          <div class="ops-grid ops-grid-2">
            <button class="btn" :disabled="!canReadBalances || readingBalances" @click="refreshBalancesOnce">
              {{ readingBalances ? "REFRESHING..." : "立即刷新" }}
            </button>

            <button class="btn" :disabled="!canReadBalances" @click="toggleBalancePoll">
              {{ balancePolling ? "自动刷新:ON" : "自动刷新:OFF" }}
            </button>
          </div>

          <div class="helper mono">
            仅读取链上余额，不需要签名。会自动读取合约中的 WBNB()/USDT() 地址；
            TargetToken =
            <span v-if="!isScanMode">你填写的 targetToken。</span>
            <span v-else>未指定（SCAN 模式显示为 - 或 0）。</span>
          </div>
        </section>

        <!-- 3) 运行状态 -->
        <section class="card">
          <div class="card-h">
            <div class="card-title">运行状态</div>
            <div class="badge">/api/jobs</div>
          </div>

          <div class="kv">
            <div class="k">jobId</div>
            <div class="v mono">{{ jobId ?? "-" }}</div>

            <div class="k">withdrawer</div>
            <div class="v mono ell">{{ userWallet || "-" }}</div>

            <div class="k">targetToken</div>
            <div class="v mono ell">
              {{ isScanMode ? "(SCAN MODE)" : (form.targetToken || "-") }}
            </div>

            <div class="k">payToken</div>
            <div class="v mono">{{ (result.payToken ?? form.payToken) || "-" }}</div>

            <div class="k">trader</div>
            <div class="v mono ell">{{ traderWallet || "-" }}</div>

            <div class="k">contract</div>
            <div class="v mono ell">{{ deployedContract || "-" }}</div>

            <div class="k">status</div>
            <div class="v mono" :class="statusClass">{{ result.status ?? "-" }}</div>

            <div class="k">message</div>
            <div class="v mono wraptext">{{ result.message ?? "-" }}</div>
          </div>

          <div class="ops-grid">
            <button class="btn" :disabled="!jobId" @click="fetchJob">刷新</button>

            <button class="btn" :disabled="!jobId" @click="togglePoll">
              {{ polling ? "自动刷新:ON" : "自动刷新:OFF" }}
            </button>

            <button class="btn" :disabled="!canCancelJob" @click="onCancelJob">
              {{ canceling ? "CANCELING..." : "取消任务" }}
            </button>

            <button class="btn" @click="copyText(JSON.stringify(result, null, 2), '已复制结果')">复制</button>
          </div>

        </section>

        <!-- 4) 提取设置 -->
        <section class="card">
          <div class="card-h">
            <div class="card-title">提取代币</div>
            <div class="badge mono">withdraw(t)</div>
          </div>

          <div class="form">
            <label class="field">
              <span class="label">提取 Token 地址（t）</span>
              <input
                  v-model.trim="withdrawToken"
                  class="input mono"
                  placeholder="留空或 0x000..000 表示提取 BNB；填 ERC20 地址表示提取该 Token"
              />
            </label>

            <div class="helper mono">
              onlyWithdrawer：必须由当前 withdrawer 钱包签名。合约余额会被转到 withdrawer。
            </div>
          </div>
        </section>

        <!-- 4.5) 紧急卖出 -->
        <section class="card">
          <div class="card-h">
            <div class="card-title">紧急卖出</div>
            <div class="badge mono">auto pair · tokenIn=targetToken</div>
          </div>

          <div class="form">
            <div class="helper mono" v-if="isScanMode">
              扫描模式未指定 targetToken，无法执行“按 targetToken 紧急卖出”。如需紧急处置，请切回“指定 targetToken”模式，
              或由后端提供扫描模式的应急接口。
            </div>
            <div class="helper mono" v-else>
              系统会根据 payToken 自动选择 baseToken（USDT/WBNB），用 PancakeFactory.getPair(targetToken, baseToken) 自动查
              pair，
              然后调用 emergencySellByWithdrawer(pair, targetToken, true)。
            </div>
          </div>
        </section>

        <!-- 5) 控制台 -->
        <section class="card">
          <div class="card-h">
            <div class="card-title">系统信息</div>
            <div class="badge mono">API=/api</div>
          </div>

          <div class="console mono" ref="consoleEl">
            <div class="line dim">[sys] ready. set mode -> connect -> fetch trader -> deploy -> start</div>
            <div v-for="(l, idx) in logs" :key="idx" class="line" :class="l.level">
              {{ l.text }}
            </div>
          </div>

          <div class="ops-grid">
            <button class="btn" @click="clearLogs">清除</button>
            <button class="btn" @click="saveDraft">保存</button>
            <button class="btn" @click="loadDraft">加载</button>
          </div>
        </section>
      </div>
    </main>

    <footer class="bottom-bar bottom-bar-3" ref="bottomBarEl">
      <button class="primary" :disabled="starting" @click="onStartBot">
        {{ starting ? "EXECUTING..." : "启动机器人" }}
      </button>

      <button class="danger" :disabled="starting || deploying || withdrawing" @click="onWithdraw">
        {{ withdrawing ? "WITHDRAWING..." : "提取代币" }}
      </button>

      <button class="danger" :disabled="!canEmergencySell || emergencySelling" @click="onEmergencySell">
        {{ emergencySelling ? "SELLING..." : "紧急卖出" }}
      </button>
    </footer>

    <div class="toast-wrap" v-if="toastState.show">
      <div class="toast" :class="toastState.type">
        <div class="toast-title mono">{{ toastState.title }}</div>
        <div class="toast-msg mono">{{ toastState.message }}</div>
      </div>
    </div>

    <div class="scanlines"/>
  </div>
</template>

<script>
import {ethers} from "ethers"; // ethers v5
import {UNIVERSAL_SNIPER_ABI, UNIVERSAL_SNIPER_BYTECODE} from "@/contracts/universalSniper";
import {http} from "@/api/http";

// Pancake factory
const FACTORY_ABI = ["function getPair(address tokenA,address tokenB) external view returns (address pair)"];

// ERC20 minimal
const ERC20_ABI_MIN = [
  "function balanceOf(address) view returns (uint256)",
  "function decimals() view returns (uint8)"
];

// ✅ 可选：如果你合约实现了这些 view，前端会自动优先用它们；否则回退 ERC20.balanceOf
const CONTRACT_BALANCE_VIEW_ABI = [
  "function getContractTokenBalance(address token) view returns (uint256)",
  "function getBalances(address targetToken) view returns (uint256 wbnbBal,uint256 usdtBal,uint256 targetBal)"
];

const PANCAKE_FACTORY_BY_CHAIN = {
  56: "0xca143ce32fe78f1f7019d7d551a6402fc5350c73",
  97: "0x6725F303b657a9451d8BA641348b6761A6CC7a17"
};

export default {
  name: "BotDeploy",
  data() {
    return {
      hintOn: true,

      userWallet: "",
      chainId: null,
      connecting: false,

      traderWallet: "",
      loadingTrader: false,

      deploying: false,
      deployStatus: "IDLE",
      deployTxHash: "",
      deployedContract: "",

      starting: false,
      withdrawing: false,
      emergencySelling: false,

      jobId: null,
      result: {jobId: null, status: null, message: null, payToken: null},

      polling: true,
      pollTimer: null,

      toastTimer: null,
      toastState: {show: false, type: "info", title: "INFO", message: ""},

      withdrawToken: "",

      canceling: false,

      form: {
        mode: "TARGET", // ✅ NEW: TARGET | SCAN
        targetToken: "",
        payToken: "USDT",
        amount: null,
        profitRate: null
      },

      logs: [],

      // eslint-safe: 不用 _xxx
      resizeTimer: null,

      // ✅ balances
      balances: {
        wbnbText: "-",
        usdtText: "-",
        targetText: "-",
        updatedAt: "",
        raw: {wbnb: null, usdt: null, target: null}
      },
      readingBalances: false,
      balancePolling: true,
      balanceTimer: null,

      decimalsCache: {}
    };
  },
  computed: {
    isScanMode() {
      return String(this.form.mode || "TARGET").toUpperCase() === "SCAN";
    },
    canFetchTrader() {
      return !!this.userWallet && (this.isScanMode || !!this.form.targetToken);
    },
    canDeploy() {
      // 你原逻辑要求 targetToken；现在 SCAN 模式也允许部署（部署仅依赖 trader+withdrawer）
      return !!this.userWallet && !!this.traderWallet;
    },
    canEmergencySell() {
      if (this.isScanMode) return false; // ✅ SCAN 模式禁用
      return !!this.userWallet && !!this.deployedContract && !!this.form.targetToken;
    },
    canReadBalances() {
      return (
          !!this.deployedContract &&
          /^0x[a-fA-F0-9]{40}$/.test(this.deployedContract) &&
          !!this.chainId &&
          !!window.ethereum
      );
    },
    statusClass() {
      const s = (this.result.status || "").toUpperCase();
      if (s === "RUNNING") return "ok";
      if (s === "PENDING" || s === "CLAIMED") return "warn";
      if (s === "STOPPED") return "err";
      if (s === "FAILED" || s === "CANCELED" || s === "REJECTED") return "err";
      return "dim";
    },
    deployStatusClass() {
      const s = (this.deployStatus || "").toUpperCase();
      if (s === "DEPLOYED") return "ok";
      if (s === "DEPLOYING") return "warn";
      if (s === "FAILED") return "err";
      return "dim";
    },
    chainLabel() {
      if (!this.chainId) return "-";
      const id = Number(this.chainId);
      if (id === 56) return "BSC Mainnet (56)";
      if (id === 97) return "BSC Testnet (97)";
      return `Chain (${id})`;
    },
    isFinalLocked() {
      const s = String(this.result?.status || "").toUpperCase();
      // 终态锁死：cancel/stop 后不可再 start（你要求的）
      return ["CANCELED", "STOPPED"].includes(s);
    },
    canCancelJob() {
      if (!this.jobId) return false;
      const s = String(this.result?.status || "").toUpperCase();
      return ["PENDING", "CLAIMED", "RUNNING"].includes(s);
    }
  },
  watch: {
    deployedContract() {
      this.resetBalanceDisplay();
      this.restartBalancePollIfNeeded();
      this.$nextTick(() => this.syncBottomBarHeight());
    },
    chainId() {
      this.resetBalanceDisplay();
      this.restartBalancePollIfNeeded();
    },
    "form.targetToken"() {
      this.resetBalanceDisplay(false);
      if (this.balancePolling) this.refreshBalancesOnce();
    },
    "form.mode"() {
      // mode 改变后 UI 高度同步 + 余额刷新策略更新
      this.resetBalanceDisplay(false);
      if (this.balancePolling) this.refreshBalancesOnce();
      this.$nextTick(() => this.syncBottomBarHeight());
    }
  },
  mounted() {
    this.initWalletEvents();
    this.tryAutoConnect();

    this.$nextTick(() => this.syncBottomBarHeight());
    window.addEventListener("resize", this.onResize, {passive: true});

    this.restartBalancePollIfNeeded();
  },
  beforeUnmount() {
    this.stopPoll();
    this.stopBalancePoll();
    if (this.toastTimer) clearTimeout(this.toastTimer);
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    window.removeEventListener("resize", this.onResize);
    this.unbindWalletEvents();
  },
  methods: {
    // =======================
    // mode
    // =======================
    setMode(m) {
      const next = String(m || "TARGET").toUpperCase();
      if (!["TARGET", "SCAN"].includes(next)) return;
      if (this.form.mode === next) return;

      this.form.mode = next;

      if (next === "SCAN") {
        // 清空 targetToken，避免误解
        this.form.targetToken = "";
      }

      this.toast(next === "SCAN" ? "已切换：全网扫描模式" : "已切换：指定 targetToken 模式", "info");
      this.saveCacheByWallet();
      this.$nextTick(() => this.syncBottomBarHeight());
    },

    // =======================
    // bottom bar height sync
    // =======================
    onResize() {
      if (this.resizeTimer) clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.syncBottomBarHeight(), 80);
    },
    onLayoutMaybeChanged() {
      this.$nextTick(() => this.syncBottomBarHeight());
    },
    syncBottomBarHeight() {
      const bar = this.$refs.bottomBarEl;
      const root = this.$el;
      if (!bar || !root) return;
      const h = Math.ceil(bar.getBoundingClientRect().height || 0);
      root.style.setProperty("--bottom-bar-h", `${Math.max(68, h)}px`);
    },

    // =======================
    // balances (10s polling)
    // =======================
    resetBalanceDisplay(resetText = true) {
      if (resetText) {
        this.balances.wbnbText = "-";
        this.balances.usdtText = "-";
        this.balances.targetText = "-";
      }
      this.balances.updatedAt = "";
      this.balances.raw = {wbnb: null, usdt: null, target: null};
    },
    toggleBalancePoll() {
      this.balancePolling = !this.balancePolling;
      if (this.balancePolling) this.restartBalancePollIfNeeded();
      else this.stopBalancePoll();
      this.toast(this.balancePolling ? "余额自动刷新已开启" : "余额自动刷新已关闭", "info");
    },
    stopBalancePoll() {
      if (this.balanceTimer) {
        clearInterval(this.balanceTimer);
        this.balanceTimer = null;
      }
    },
    restartBalancePollIfNeeded() {
      this.stopBalancePoll();
      if (!this.balancePolling) return;
      if (!this.canReadBalances) return;

      this.refreshBalancesOnce();

      // ✅ 10 秒刷新一次
      this.balanceTimer = setInterval(() => {
        this.refreshBalancesOnce();
      }, 10000);
    },
    getReadProvider() {
      return new ethers.providers.Web3Provider(window.ethereum);
    },
    async getDecimals(tokenAddr, provider) {
      const a = String(tokenAddr || "").toLowerCase();
      if (!a || a === ethers.constants.AddressZero) return 18;
      if (this.decimalsCache[a] != null) return this.decimalsCache[a];

      try {
        const erc20 = new ethers.Contract(tokenAddr, ERC20_ABI_MIN, provider);
        const d = await erc20.decimals();
        const dn = Number(d);
        this.decimalsCache[a] = Number.isFinite(dn) ? dn : 18;
      } catch {
        this.decimalsCache[a] = 18;
      }
      return this.decimalsCache[a];
    },
    formatUnitsSafe(bn, decimals) {
      try {
        return ethers.utils.formatUnits(bn || 0, decimals);
      } catch {
        return "0";
      }
    },
    shortNum(s, maxDp = 6) {
      const str = String(s || "0");
      if (!str.includes(".")) return str;
      const [i, d] = str.split(".");
      return `${i}.${(d || "").slice(0, maxDp)}`.replace(/\.$/, "");
    },
    async refreshBalancesOnce() {
      if (!this.canReadBalances) return;

      const target = (this.form.targetToken || "").trim();
      const hasTarget = !this.isScanMode && /^0x[a-fA-F0-9]{40}$/.test(target);

      if (this.readingBalances) return;
      this.readingBalances = true;

      try {
        const provider = this.getReadProvider();

        const c = new ethers.Contract(this.deployedContract, UNIVERSAL_SNIPER_ABI, provider);
        const cView = new ethers.Contract(
            this.deployedContract,
            [...UNIVERSAL_SNIPER_ABI, ...CONTRACT_BALANCE_VIEW_ABI],
            provider
        );

        const [wbnbAddr, usdtAddr] = await Promise.all([c.WBNB(), c.USDT()]);

        const readViaViewOrErc20 = async (tokenAddr) => {
          // 优先：getContractTokenBalance(token)
          try {
            if (cView.getContractTokenBalance) {
              const v = await cView.getContractTokenBalance(tokenAddr);
              if (v != null) return v;
            }
          } catch {
            // fallback
          }
          // 回退：ERC20.balanceOf(contract)
          const erc20 = new ethers.Contract(tokenAddr, ERC20_ABI_MIN, provider);
          return await erc20.balanceOf(this.deployedContract);
        };

        let bWBNB, bUSDT, bT;
        let usedBundleView = false;

        // 优先：getBalances(targetToken) 一口气拿三个
        if (hasTarget) {
          try {
            if (cView.getBalances) {
              const r = await cView.getBalances(target);
              bWBNB = r.wbnbBal ?? r[0];
              bUSDT = r.usdtBal ?? r[1];
              bT = r.targetBal ?? r[2];
              usedBundleView = true;
            }
          } catch {
            // fallback
          }
        }

        if (!usedBundleView) {
          [bWBNB, bUSDT, bT] = await Promise.all([
            readViaViewOrErc20(wbnbAddr),
            readViaViewOrErc20(usdtAddr),
            hasTarget ? readViaViewOrErc20(target) : Promise.resolve(ethers.constants.Zero)
          ]);
        }

        const [dWBNB, dUSDT, dT] = await Promise.all([
          this.getDecimals(wbnbAddr, provider),
          this.getDecimals(usdtAddr, provider),
          hasTarget ? this.getDecimals(target, provider) : Promise.resolve(18)
        ]);

        const wbnbText = this.shortNum(this.formatUnitsSafe(bWBNB, dWBNB));
        const usdtText = this.shortNum(this.formatUnitsSafe(bUSDT, dUSDT));
        const targetText = hasTarget ? this.shortNum(this.formatUnitsSafe(bT, dT)) : "-";

        const t = new Date().toLocaleTimeString();

        this.balances = {
          wbnbText,
          usdtText,
          targetText,
          updatedAt: t,
          raw: {
            wbnb: {token: wbnbAddr, balance: bWBNB.toString(), decimals: dWBNB},
            usdt: {token: usdtAddr, balance: bUSDT.toString(), decimals: dUSDT},
            target: hasTarget ? {token: target, balance: bT.toString(), decimals: dT} : null
          }
        };
      } catch (e) {
        const msg = e?.data?.message || e?.error?.message || e?.message || "read balances failed";
        this.log(`[balances] ERROR: ${msg}`, "warn");
      } finally {
        this.readingBalances = false;
      }
    },

    // =======================
    // local cache by wallet
    // =======================
    cacheKeyByWallet(addr) {
      const a = String(addr || "").toLowerCase();
      return a ? `BOT_DEPLOY_CACHE_${a}` : null;
    },
    saveCacheByWallet() {
      const key = this.cacheKeyByWallet(this.userWallet);
      if (!key) return;

      const payload = {
        hintOn: this.hintOn,
        chainId: this.chainId,
        traderWallet: this.traderWallet,
        deployedContract: this.deployedContract,
        deployTxHash: this.deployTxHash,
        deployStatus: this.deployStatus,
        form: this.form,
        jobId: this.jobId,
        result: this.result,
        polling: this.polling,
        withdrawToken: this.withdrawToken
      };
      localStorage.setItem(key, JSON.stringify(payload));
      this.log(`cache saved by wallet: ${key}`, "dim");
    },
    restoreByWallet(addr) {
      const key = this.cacheKeyByWallet(addr);
      if (!key) return;

      try {
        const raw = localStorage.getItem(key);
        if (!raw) return;
        const d = JSON.parse(raw);

        if (typeof d.hintOn === "boolean") this.hintOn = d.hintOn;
        if (d.chainId) this.chainId = d.chainId;

        if (d.traderWallet) this.traderWallet = d.traderWallet;
        if (d.deployedContract) this.deployedContract = d.deployedContract;
        if (d.deployTxHash) this.deployTxHash = d.deployTxHash;
        if (d.deployStatus) this.deployStatus = d.deployStatus;

        if (d.form) this.form = {...this.form, ...d.form};
        if (d.jobId) this.jobId = d.jobId;
        if (d.result) this.result = d.result;
        if (typeof d.polling === "boolean") this.polling = d.polling;
        if (typeof d.withdrawToken === "string") this.withdrawToken = d.withdrawToken;

        this.log(`cache restored: ${key}`, "dim");
        this.toast("已按钱包恢复上次数据", "info");

        if (this.polling && this.jobId) this.startPoll();
      } catch (e) {
        console.log(e);
        this.toast("缓存解析失败", "warn");
      } finally {
        this.$nextTick(() => this.syncBottomBarHeight());
      }
    },

    // =======================
    // basic ui
    // =======================
    log(msg, level = "dim") {
      const t = new Date().toLocaleTimeString();
      this.logs.push({text: `[${t}] ${msg}`, level});
      if (this.logs.length > 220) this.logs = this.logs.slice(-220);
      this.$nextTick(() => {
        const el = this.$refs.consoleEl;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    toast(message, type = "info", title) {
      if (this.toastTimer) clearTimeout(this.toastTimer);
      const map = {success: "SUCCESS", error: "ERROR", warn: "WARNING", info: "INFO"};
      this.toastState.type = type;
      this.toastState.title = title || map[type] || "INFO";
      this.toastState.message = String(message || "");
      this.toastState.show = true;
      this.toastTimer = setTimeout(() => (this.toastState.show = false), 1800);
    },
    async copyText(text, okMsg = "已复制") {
      try {
        await navigator.clipboard.writeText(String(text || ""));
        this.toast(okMsg, "success");
      } catch {
        this.toast("复制失败（浏览器权限限制）", "warn");
      }
    },
    openExplorer(addr) {
      if (!addr) return;
      const id = Number(this.chainId);
      const base = id === 97 ? "https://testnet.bscscan.com" : "https://bscscan.com";
      window.open(`${base}/address/${addr}`, "_blank");
    },

    validateTargetToken() {
      if (this.isScanMode) return null; // ✅ SCAN 不校验
      const v = (this.form.targetToken || "").trim();
      if (!/^0x[a-fA-F0-9]{40}$/.test(v)) return "targetToken 不合法";
      return null;
    },
    validateStart() {
      const e1 = this.validateTargetToken();
      if (e1) return e1;
      if (!this.userWallet) return "请先连接钱包（withdrawer）";
      if (!this.traderWallet) return "请先获取 trader 地址";
      if (!this.deployedContract) return "请先部署合约并获得合约地址";
      if (!this.form.amount || Number(this.form.amount) <= 0) return "购买数量必须 > 0";
      if (!this.form.profitRate || Number(this.form.profitRate) <= 0) return "赢利率必须 > 0";
      const pt = String(this.form.payToken || "").toUpperCase();
      if (!["USDT", "BNB"].includes(pt)) return "payToken 必须为 USDT 或 BNB";
      return null;
    },

    // =======================
    // wallet
    // =======================
    getProvider() {
      return window.ethereum;
    },
    async tryAutoConnect() {
      const eth = this.getProvider();
      if (!eth) return;
      try {
        const accounts = await eth.request({method: "eth_accounts"});
        if (accounts && accounts[0]) {
          this.userWallet = accounts[0];
          this.chainId = await eth.request({method: "eth_chainId"});
          this.log(`wallet auto-connected: ${this.userWallet}`, "ok");
          this.restoreByWallet(this.userWallet);
        }
      } catch {
        // ignore
      } finally {
        this.$nextTick(() => this.syncBottomBarHeight());
      }
    },
    async connectWallet() {
      const eth = this.getProvider();
      if (!eth) return this.toast("未检测到钱包（MetaMask）", "warn");

      this.connecting = true;
      try {
        const accounts = await eth.request({method: "eth_requestAccounts"});
        this.userWallet = accounts?.[0] || "";
        this.chainId = await eth.request({method: "eth_chainId"});
        if (!this.userWallet) throw new Error("连接失败：未返回账户");

        this.log(`wallet connected: ${this.userWallet}`, "ok");
        this.toast("钱包已连接", "success");

        this.restoreByWallet(this.userWallet);
      } catch (e) {
        const msg = e?.message || "wallet connect failed";
        this.log(`ERROR: ${msg}`, "err");
        this.toast(msg, "error");
      } finally {
        this.connecting = false;
        this.$nextTick(() => this.syncBottomBarHeight());
      }
    },
    initWalletEvents() {
      const eth = this.getProvider();
      if (!eth || !eth.on) return;

      this.onAccountsChanged = (accs) => {
        const next = accs?.[0] || "";
        this.userWallet = next;
        this.log(`accountsChanged: ${next || "(disconnected)"}`, next ? "ok" : "warn");

        this.stopPoll();
        this.stopBalancePoll();
        this.resetBalanceDisplay();

        this.traderWallet = "";
        this.deployedContract = "";
        this.deployTxHash = "";
        this.deployStatus = "IDLE";
        this.jobId = null;
        this.result = {jobId: null, status: null, message: null, payToken: null};

        if (next) this.restoreByWallet(next);
        this.restartBalancePollIfNeeded();

        this.$nextTick(() => this.syncBottomBarHeight());
      };

      this.onChainChanged = (cid) => {
        this.chainId = cid;
        this.log(`chainChanged: ${this.chainLabel}`, "dim");
        this.toast(`网络切换：${this.chainLabel}`, "info");

        this.deployedContract = "";
        this.deployTxHash = "";
        this.deployStatus = "IDLE";

        this.stopBalancePoll();
        this.resetBalanceDisplay();

        this.saveCacheByWallet();
        this.restartBalancePollIfNeeded();

        this.$nextTick(() => this.syncBottomBarHeight());
      };

      eth.on("accountsChanged", this.onAccountsChanged);
      eth.on("chainChanged", this.onChainChanged);
    },
    unbindWalletEvents() {
      const eth = this.getProvider();
      if (!eth || !eth.removeListener) return;
      if (this.onAccountsChanged) eth.removeListener("accountsChanged", this.onAccountsChanged);
      if (this.onChainChanged) eth.removeListener("chainChanged", this.onChainChanged);
    },

    // =======================
    // trader
    // =======================
    async fetchTraderWallet() {
      // ✅ SCAN 模式不要求 targetToken
      if (!this.isScanMode) {
        const e1 = this.validateTargetToken();
        if (e1) return this.toast(e1, "warn");
      }
      if (!this.userWallet) return this.toast("请先连接钱包", "warn");

      this.loadingTrader = true;
      try {
        this.log("GET /api/trader-wallet", "dim");

        const params = {
          withdrawer: this.userWallet,
          mode: this.isScanMode ? "SCAN" : "TARGET"
        };
        if (!this.isScanMode) params.targetToken = this.form.targetToken;

        const resp = await http.get("/trader-wallet", {params});

        const tw = resp?.data?.traderWallet || resp?.data?.address || "";
        if (!tw) throw new Error("后端未返回 traderWallet");

        this.traderWallet = tw;
        this.log(`traderWallet=${tw}`, "ok");
        this.toast("已获取 trader 地址", "success");

        this.saveCacheByWallet();
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "fetch trader failed";
        this.log(`ERROR: ${msg}`, "err");
        this.toast(msg, "error");
      } finally {
        this.loadingTrader = false;
        this.$nextTick(() => this.syncBottomBarHeight());
      }
    },

    // =======================
    // deploy
    // =======================
    async deployContract() {
      // ✅ SCAN 模式允许部署，不再强制校验 targetToken
      if (!this.isScanMode) {
        const e1 = this.validateTargetToken();
        if (e1) return this.toast(e1, "warn");
      }

      if (!this.userWallet) return this.toast("请先连接钱包", "warn");
      if (!this.traderWallet) return this.toast("请先获取 trader 地址", "warn");

      if (!UNIVERSAL_SNIPER_BYTECODE || UNIVERSAL_SNIPER_BYTECODE.includes("__REPLACE_")) {
        return this.toast("请先在 src/contracts/universalSniper.js 中替换 BYTECODE", "warn");
      }

      try {
        this.deploying = true;
        this.deployStatus = "DEPLOYING";
        this.deployTxHash = "";
        this.deployedContract = "";

        this.log("deploy => preparing provider/signer (ethers v5)...", "dim");

        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const signer = provider.getSigner();

        const signerAddr = await signer.getAddress();
        if (signerAddr.toLowerCase() !== this.userWallet.toLowerCase()) {
          this.userWallet = signerAddr;
          this.log(`wallet sync => ${signerAddr}`, "dim");
        }

        this.log(`deploy => args(trader=${this.traderWallet}, withdrawer=${this.userWallet})`, "dim");

        const factory = new ethers.ContractFactory(UNIVERSAL_SNIPER_ABI, UNIVERSAL_SNIPER_BYTECODE, signer);
        const contract = await factory.deploy(this.traderWallet, this.userWallet);

        this.deployTxHash = contract.deployTransaction?.hash || "";
        if (this.deployTxHash) this.log(`deploy tx: ${this.deployTxHash}`, "warn");

        this.toast("已发起部署交易，请在钱包确认", "info");
        await contract.deployed();

        this.deployedContract = contract.address;
        this.deployStatus = "DEPLOYED";
        this.log(`deployed => ${contract.address}`, "ok");
        this.toast("部署成功", "success");

        this.saveCacheByWallet();
      } catch (e) {
        const msg = e?.data?.message || e?.message || "deploy failed";
        this.deployStatus = "FAILED";
        this.log(`DEPLOY ERROR: ${msg}`, "err");
        this.toast(msg, "error");
      } finally {
        this.deploying = false;
        this.$nextTick(() => this.syncBottomBarHeight());
      }
    },

    // =======================
    // start bot
    // =======================
    async onStartBot() {
      // 终态锁死：cancel/stop 后不可再启动（前端先拦一次）
      if (this.isFinalLocked) {
        return this.toast(`任务已终态锁定（${this.result.status}），无法重新启动`, "warn");
      }

      // 如果当前已 RUNNING，也不允许再次 start（避免重复任务）
      const cur = String(this.result?.status || "").toUpperCase();
      if (cur === "RUNNING") {
        return this.toast("任务正在 RUNNING，无法重复启动", "warn");
      }

      const err = this.validateStart();
      if (err) return this.toast(err, "warn");

      // ✅ 点击启动就保存一次（你要求“机器人启动时也要保存”）
      this.saveCacheByWallet();

      this.starting = true;
      try {
        this.log("POST /api/start", "dim");

        const payload = {
          withdrawer: this.userWallet,
          mode: this.isScanMode ? "SCAN" : "TARGET",
          traderWallet: this.traderWallet,
          contractAddress: this.deployedContract,
          amount: Number(this.form.amount),
          profitRate: Number(this.form.profitRate),
          payToken: String(this.form.payToken || "USDT").toUpperCase()
        };

        if (!this.isScanMode) {
          payload.targetToken = this.form.targetToken;
        }

        const resp = await http.post("/start", payload);

        this.jobId = resp.data.jobId || resp.data.id || null;
        this.result = resp.data;

        this.log(`jobId=${this.jobId}`, "ok");
        if (this.result.status) this.log(`status=${this.result.status}`, "dim");
        if (this.result.message) this.log(`message=${this.result.message}`, "dim");

        this.toast("已提交启动任务", "success");

        // ✅ 启动成功返回再保存一次
        this.saveCacheByWallet();

        if (this.polling && this.jobId) this.startPoll();
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "start failed";
        this.log(`START ERROR: ${msg}`, "err");
        this.toast(msg, "error");
        // ✅ 启动失败也保存（可选：你说“启动时也要保存”，这里也落一份）
        this.saveCacheByWallet();
      } finally {
        this.starting = false;
        this.$nextTick(() => this.syncBottomBarHeight());
      }
    },

    async onCancelJob() {
      if (!this.canCancelJob) return;

      const s = String(this.result?.status || "").toUpperCase();
      if (!["PENDING", "CLAIMED", "RUNNING"].includes(s)) {
        return this.toast(`当前状态 ${s} 不允许取消`, "warn");
      }

      this.canceling = true;
      try {
        this.log(`POST /api/jobs/${this.jobId}/cancel`, "dim");
        const resp = await http.post(`/jobs/${this.jobId}/cancel`, {message: "canceled by user"});
        // resp: {jobId, status, message}
        this.result = {...this.result, ...(resp.data || {}), status: resp.data?.status || "CANCELED"};
        this.toast("任务已取消（CANCELED）", "success");
        this.stopPoll();
        this.saveCacheByWallet();
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "cancel failed";
        this.log(`CANCEL ERROR: ${msg}`, "err");
        this.toast(msg, "error");
      } finally {
        this.canceling = false;
      }
    },

    // =======================
    // withdraw
    // =======================
    normalizeWithdrawToken(input) {
      const t = String(input || "").trim();
      if (!t) return ethers.constants.AddressZero;
      if (t === "0x0000000000000000000000000000000000000000") return ethers.constants.AddressZero;
      if (!/^0x[a-fA-F0-9]{40}$/.test(t)) throw new Error("Token 地址不合法");
      return t;
    },
    async onWithdraw() {
      if (!this.userWallet) return this.toast("请先连接钱包", "warn");
      if (!this.deployedContract) return this.toast("请先部署合约并填写合约地址", "warn");
      if (!/^0x[a-fA-F0-9]{40}$/.test(this.deployedContract)) return this.toast("合约地址不合法", "warn");

      try {
        this.withdrawing = true;

        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const signer = provider.getSigner();
        const signerAddr = await signer.getAddress();
        if (signerAddr.toLowerCase() !== this.userWallet.toLowerCase()) {
          this.userWallet = signerAddr;
          this.log(`wallet sync => ${signerAddr}`, "dim");
        }

        const t = this.normalizeWithdrawToken(this.withdrawToken);
        const tokenLabel = t === ethers.constants.AddressZero ? "BNB(native)" : t;

        this.log(`withdraw => contract=${this.deployedContract} t=${tokenLabel}`, "warn");
        this.toast("已发起提取交易，请在钱包确认", "info");

        const c = new ethers.Contract(this.deployedContract, UNIVERSAL_SNIPER_ABI, signer);
        const tx = await c.withdraw(t);
        this.log(`withdraw tx: ${tx.hash}`, "warn");

        const rcpt = await tx.wait();
        this.log(`withdraw confirmed: ${rcpt.transactionHash}`, "ok");
        this.toast("提取成功", "success");

        this.saveCacheByWallet();
      } catch (e) {
        const msg = e?.data?.message || e?.error?.message || e?.message || "withdraw failed";
        this.log(`WITHDRAW ERROR: ${msg}`, "err");
        this.toast(msg, "error");
      } finally {
        this.withdrawing = false;
        this.$nextTick(() => this.syncBottomBarHeight());
      }
    },

    // =======================
    // emergency sell
    // =======================
    getFactoryAddress() {
      const id = Number(this.chainId);
      return PANCAKE_FACTORY_BY_CHAIN[id] || PANCAKE_FACTORY_BY_CHAIN[56];
    },
    async getBaseTokenFromContract(payTokenUpper) {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const c = new ethers.Contract(this.deployedContract, UNIVERSAL_SNIPER_ABI, provider);
      if (payTokenUpper === "USDT") return await c.USDT();
      return await c.WBNB();
    },
    async getPairAddress(tokenA, tokenB) {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const factoryAddr = this.getFactoryAddress();
      const factory = new ethers.Contract(factoryAddr, FACTORY_ABI, provider);
      return await factory.getPair(tokenA, tokenB);
    },
    async onEmergencySell() {
      if (this.isScanMode) return this.toast("SCAN 模式未指定 targetToken，无法紧急卖出", "warn");

      const e1 = this.validateTargetToken();
      if (e1) return this.toast(e1, "warn");
      if (!this.userWallet) return this.toast("请先连接钱包", "warn");
      if (!this.deployedContract) return this.toast("请先部署合约并获得合约地址", "warn");
      if (!/^0x[a-fA-F0-9]{40}$/.test(this.deployedContract)) return this.toast("合约地址不合法", "warn");

      try {
        this.emergencySelling = true;

        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const signer = provider.getSigner();

        const signerAddr = await signer.getAddress();
        if (signerAddr.toLowerCase() !== this.userWallet.toLowerCase()) {
          this.userWallet = signerAddr;
          this.log(`wallet sync => ${signerAddr}`, "dim");
        }

        const tokenIn = this.form.targetToken.trim();
        const pt = String(this.form.payToken || "USDT").toUpperCase();

        this.log(`emergencySell: read baseToken from contract (payToken=${pt})`, "dim");
        const baseToken = await this.getBaseTokenFromContract(pt);

        this.log(`emergencySell: query pair getPair(tokenIn=${tokenIn}, base=${baseToken})`, "dim");
        const pair = await this.getPairAddress(tokenIn, baseToken);

        if (!pair || pair === ethers.constants.AddressZero) {
          throw new Error("未找到交易对（pair=0x0），请切换 payToken 或确认该 token 是否与 USDT/WBNB 建池");
        }

        this.log(`emergencySell: pair=${pair}`, "warn");
        this.toast("已发起紧急卖出，请在钱包确认", "info");

        const c = new ethers.Contract(this.deployedContract, UNIVERSAL_SNIPER_ABI, signer);
        const tx = await c.emergencySellByWithdrawer(pair, tokenIn, true);
        this.log(`emergencySell tx: ${tx.hash}`, "warn");

        const rcpt = await tx.wait();
        this.log(`emergencySell confirmed: ${rcpt.transactionHash}`, "ok");
        this.toast("紧急卖出完成", "success");

        // ✅ 紧急卖出后：把任务标记为 STOPPED（终态锁死，后端也会强制）
        if (this.jobId) {
          try {
            this.log(`POST /api/jobs/${this.jobId}/stop`, "dim");
            const r2 = await http.post(`/jobs/${this.jobId}/stop`, {message: "stopped by emergency sell"});
            this.result = {...this.result, ...(r2.data || {}), status: r2.data?.status || "STOPPED"};
            this.stopPoll();
            this.toast("任务已终止（STOPPED，无法重新启动）", "warn");
            this.saveCacheByWallet();
          } catch (e2) {
            const msg2 = e2?.response?.data?.detail || e2?.message || "stop update failed";
            this.log(`STOP UPDATE ERROR: ${msg2}`, "warn");
            // 不影响链上卖出结果，只是后台状态同步失败
          }
        }

        this.saveCacheByWallet();
      } catch (e) {
        const msg = e?.data?.message || e?.error?.message || e?.message || "emergency sell failed";
        this.log(`EMERGENCY SELL ERROR: ${msg}`, "err");
        this.toast(msg, "error");
      } finally {
        this.emergencySelling = false;
        this.$nextTick(() => this.syncBottomBarHeight());
      }
    },

    // =======================
    // job polling
    // =======================
    async fetchJob() {
      if (!this.jobId) return;

      try {
        this.log(`GET /api/jobs/${this.jobId}`, "dim");
        const resp = await http.get(`/jobs/${this.jobId}`);
        this.result = resp.data;

        const s = (this.result.status || "").toUpperCase();
        this.log(`status=${s || "-"}`, s === "RUNNING" ? "ok" : "dim");
        if (this.result.message) this.log(`message=${this.result.message}`, "dim");

        if (["DONE", "FAILED", "CANCELED", "STOPPED"].includes(s)) {
          this.stopPoll();
          this.log(`poll stopped (final: ${s})`, "dim");
        }

        this.saveCacheByWallet();
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "poll failed";
        this.log(`POLL ERROR: ${msg}`, "err");
      }
    },
    startPoll() {
      this.stopPoll();
      this.pollTimer = setInterval(this.fetchJob, 2500);
      this.log("poll started: 2500ms", "dim");
    },
    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    togglePoll() {
      this.polling = !this.polling;
      if (this.polling && this.jobId) this.startPoll();
      else this.stopPoll();
      this.toast(this.polling ? "轮询已开启" : "轮询已关闭", "info");
      this.saveCacheByWallet();
    },

    clearLogs() {
      this.logs = [];
      this.log("logs cleared", "dim");
      this.toast("已清空日志", "info");
    },

    // =======================
    // manual save/load
    // =======================
    saveDraft() {
      this.saveCacheByWallet();
      this.toast("已保存（按钱包缓存）", "success");
    },
    loadDraft() {
      if (!this.userWallet) return this.toast("请先连接钱包再加载", "warn");
      this.restoreByWallet(this.userWallet);
    }
  }
};
</script>

<style lang="less" scoped>
@bg: #070a0f;
@panel: rgba(12, 18, 30, 0.82);
@panel2: rgba(0, 0, 0, 0.25);
@line: rgba(0, 255, 170, 0.18);
@neon: #00ffaa;
@text: rgba(255, 255, 255, 0.9);
@muted: rgba(255, 255, 255, 0.58);

.page {
  min-height: 100dvh;
  --bottom-bar-h: 68px;

  background: radial-gradient(900px 600px at 15% 10%, rgba(0, 255, 170, 0.08), transparent 60%),
  radial-gradient(900px 600px at 85% 20%, rgba(90, 140, 255, 0.07), transparent 60%),
  radial-gradient(900px 700px at 50% 95%, rgba(255, 0, 150, 0.05), transparent 60%),
  @bg;
  color: @text;
  display: grid;
  grid-template-rows: auto 1fr auto;

  :deep(*) {
    box-sizing: border-box;
  }
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid @line;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  .logo {
    width: 28px;
    height: 28px;
    border-radius: 8px; // 如果你想要圆形：改成 50%
    object-fit: contain;
    box-shadow: 0 0 18px rgba(0, 255, 170, 0.35);
  }

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 999px;
    background: @neon;
    box-shadow: 0 0 16px rgba(0, 255, 170, 0.55);
  }

  .titles {
    min-width: 0;

    .name {
      letter-spacing: 0.12em;
      font-weight: 900;
      font-size: 13px;
    }

    .sub {
      margin-top: 2px;
      font-size: 11px;
      color: @muted;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.ghost {
  flex: none;
  border: 1px solid @line;
  color: rgba(210, 255, 238, 0.9);
  background: rgba(0, 255, 170, 0.06);
  border-radius: 12px;
  padding: 8px 10px;
  font-size: 12px;
  white-space: nowrap;
}

.scroll {
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

.content {
  display: grid;
  gap: 12px;
  max-width: 680px;
  margin: 0 auto;
  padding: 14px 14px calc(var(--bottom-bar-h) + 14px + env(safe-area-inset-bottom));
}

.notice {
  border: 1px dashed rgba(255, 204, 0, 0.35);
  background: rgba(255, 204, 0, 0.06);
  border-radius: 14px;
  padding: 12px;

  .notice-title {
    font-weight: 800;
    letter-spacing: 0.08em;
    font-size: 12px;
    color: rgba(255, 204, 0, .95);
    margin-bottom: 6px;
  }

  .notice-text {
    font-size: 12px;
    color: rgba(255, 255, 255, .68);
    line-height: 1.5;
  }
}

.card {
  background: linear-gradient(180deg, @panel, rgba(10, 16, 26, 0.9));
  border: 1px solid @line;
  border-radius: 16px;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.45);
  padding: 12px;
  overflow: hidden;
}

.card-h {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.card-title {
  font-weight: 900;
  letter-spacing: 0.12em;
  font-size: 12px;
  text-shadow: 0 0 12px rgba(0, 255, 170, 0.22);
}

.badge {
  font-size: 11px;
  color: rgba(0, 255, 170, 0.85);
  border: 1px solid @line;
  background: rgba(0, 255, 170, 0.05);
  padding: 6px 8px;
  border-radius: 999px;
  white-space: nowrap;
}

.form {
  display: grid;
  gap: 10px;
}

.field {
  display: grid;
  gap: 6px;
  min-width: 0;

  .label {
    font-size: 11px;
    color: @muted;
    letter-spacing: 0.06em;
  }
}

.helper {
  font-size: 11px;
  color: rgba(255, 255, 255, .52);
  line-height: 1.35;
}

.row2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

@media (max-width: 380px) {
  .row2 {
    grid-template-columns: 1fr;
  }
}

/* ✅ NEW: mode toggle chips */
.mode-toggle {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.chip {
  border: 1px solid @line;
  background: rgba(0, 0, 0, 0.18);
  color: rgba(255, 255, 255, 0.72);
  padding: 10px 12px;
  border-radius: 999px;
  font-size: 12px;
  letter-spacing: 0.06em;
}

.chip.on {
  background: rgba(0, 255, 170, 0.16);
  border-color: rgba(0, 255, 170, 0.35);
  color: rgba(220, 255, 244, 0.95);
  box-shadow: 0 0 18px rgba(0, 255, 170, 0.18);
}

.input {
  width: 100%;
  min-width: 0;
  padding: 12px 12px;
  border-radius: 14px;
  border: 1px solid @line;
  background: @panel2;
  color: @text;
  outline: none;
  font-size: 13px;
}

.kv {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 10px;
  border: 1px solid @line;
  border-radius: 14px;
  background: rgba(0, 0, 0, 0.22);
  padding: 12px;
  min-width: 0;

  .k {
    color: @muted;
    font-size: 11px;
  }

  .v {
    font-size: 12px;
    min-width: 0;
  }

  .wraptext {
    white-space: normal;
    word-break: break-word;
  }

  .ell {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.ok {
  color: rgba(0, 255, 170, 0.95);
}

.warn {
  color: rgba(255, 204, 0, 0.95);
}

.err {
  color: rgba(255, 120, 120, 0.95);
}

.dim {
  color: rgba(255, 255, 255, 0.62);
}

.ops-grid {
  margin-top: 10px;
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.ops-grid-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (max-width: 520px) {
  .ops-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .ops-grid > :last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }
}

@media (max-width: 360px) {
  .ops-grid {
    grid-template-columns: 1fr;
  }

  .ops-grid > :last-child:nth-child(odd) {
    grid-column: auto;
  }
}

.btn {
  width: 100%;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid @line;
  background: rgba(0, 255, 170, 0.06);
  color: rgba(210, 255, 238, 0.92);
  font-size: 12px;
}

.console {
  border: 1px solid @line;
  background: rgba(0, 0, 0, 0.28);
  border-radius: 14px;
  padding: 10px;
  max-height: clamp(180px, 32vh, 320px);
  overflow: auto;

  .line {
    line-height: 1.55;
    font-size: 12px;
  }
}

/* ✅ 底栏：减少空隙 & 自适应高度 */
.bottom-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 12;

  padding: 8px 12px calc(8px + env(safe-area-inset-bottom));
  display: grid;
  gap: 10px;

  background: rgba(0, 0, 0, 0.42);
  backdrop-filter: blur(10px);
  border-top: 1px solid @line;

  grid-template-columns: 1fr 1fr;
}

.bottom-bar-3 {
  grid-template-columns: 1fr 1fr 1fr;
}

@media (max-width: 520px) {
  .bottom-bar-3 {
    grid-template-columns: 1fr;
    align-content: start;
  }
}

.primary {
  padding: 13px 12px;
  border-radius: 16px;
  border: 1px solid rgba(0, 255, 170, 0.35);
  background: rgba(0, 255, 170, 0.16);
  color: rgba(220, 255, 244, 0.95);
  font-weight: 800;
  letter-spacing: 0.08em;
}

.danger {
  padding: 13px 12px;
  border-radius: 16px;
  border: 1px solid rgba(255, 77, 79, 0.45);
  background: rgba(255, 77, 79, 0.12);
  color: rgba(255, 200, 200, 0.95);
  font-weight: 800;
  letter-spacing: 0.08em;
}

button:disabled {
  opacity: 0.55;
}

.toast-wrap {
  position: fixed;
  left: 0;
  right: 0;
  bottom: calc(var(--bottom-bar-h) + 12px + env(safe-area-inset-bottom));
  display: flex;
  justify-content: center;
  z-index: 9999;
  pointer-events: none;
}

.toast {
  pointer-events: auto;
  width: min(520px, calc(100vw - 28px));
  border-radius: 14px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.78);
  border: 1px solid rgba(0, 255, 170, 0.25);
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(10px);
  animation: toastIn 0.18s ease-out forwards;
}

.toast-title {
  font-weight: 900;
  letter-spacing: 0.1em;
  font-size: 12px;
  margin-bottom: 4px;
  color: rgba(210, 255, 238, 0.95);
}

.toast-msg {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.45;
  word-break: break-word;
}

.toast.success {
  border-color: rgba(0, 255, 170, 0.35);
}

.toast.warn {
  border-color: rgba(255, 204, 0, 0.45);
}

.toast.error {
  border-color: rgba(255, 77, 79, 0.55);
}

.toast.info {
  border-color: rgba(120, 180, 255, 0.45);
}

@keyframes toastIn {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.scanlines {
  pointer-events: none;
  position: fixed;
  inset: 0;
  background: repeating-linear-gradient(to bottom,
  rgba(255, 255, 255, 0.02),
  rgba(255, 255, 255, 0.02) 1px,
  transparent 1px,
  transparent 4px);
  mix-blend-mode: overlay;
  opacity: 0.22;
}
</style>
